import React, { useState } from "react";
import { PartnerFilter as PartnerFilterType } from "@/types/partner";
import { RALD_REALTY_CONFIG } from "@/utils/partnerBranding";

interface PartnerFilterProps {
  filter: PartnerFilterType;
  onFilterChange: (filter: PartnerFilterType) => void;
  availablePartners?: string[];
}

const PartnerFilter: React.FC<PartnerFilterProps> = ({
  filter,
  onFilterChange,
  availablePartners = ["rald-realty"],
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleRALDRealtyToggle = () => {
    const newFilter: PartnerFilterType = {
      ...filter,
      isRALDRealtyOnly: !filter.isRALDRealtyOnly,
      selectedPartner: !filter.isRALDRealtyOnly ? "rald-realty" : undefined,
    };
    onFilterChange(newFilter);
  };

  const handlePartnerSelect = (partnerId: string) => {
    const newFilter: PartnerFilterType = {
      ...filter,
      selectedPartner:
        filter.selectedPartner === partnerId ? undefined : partnerId,
      isRALDRealtyOnly: partnerId === "rald-realty",
    };
    onFilterChange(newFilter);
  };

  const handleClearFilter = () => {
    const newFilter: PartnerFilterType = {
      ...filter,
      selectedPartner: undefined,
      isRALDRealtyOnly: false,
    };
    onFilterChange(newFilter);
  };

  const isActive = filter.isRALDRealtyOnly;

  return (
    <div className="bg-white rounded-lg border border-soft-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-main-900">Partner Filter</h3>
        {(filter.selectedPartner || filter.isRALDRealtyOnly) && (
          <button
            onClick={handleClearFilter}
            className="text-xs text-grey-7 hover:text-main-900 transition"
          >
            Clear
          </button>
        )}
      </div>

      {/* RALD REALTY Toggle */}
      <button
        onClick={handleRALDRealtyToggle}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-sm transition-all ${
          isActive
            ? "border-bnp-cyan bg-[#f0fdf4] text-main-900"
            : "border-soft-200 bg-white text-grey-7 hover:border-bnp-cyan hover:text-main-900"
        }`}
      >
        <div
          className={`w-4 h-4 shrink-0 rounded border flex items-center justify-center ${
            isActive ? "border-bnp-cyan bg-bnp-cyan" : "border-soft-200"
          }`}
        >
          {isActive && (
            <svg
              className="w-3 h-3 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>
        <span className="font-medium">{RALD_REALTY_CONFIG.name}</span>
        <span
          className={`ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded ${
            isActive ? "bg-bnp-cyan text-white" : "bg-weak-100 text-grey-7"
          }`}
        >
          Partner
        </span>
      </button>

      {/* Other Partners */}
      {availablePartners.length > 1 && (
        <div className="mt-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-between text-xs font-medium text-grey-7 hover:text-main-900"
          >
            <span>Other Partners ({availablePartners.length - 1})</span>
            <svg
              className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isExpanded && (
            <div className="mt-2 space-y-1.5">
              {availablePartners
                .filter((partner) => partner !== "rald-realty")
                .map((partnerId) => (
                  <button
                    key={partnerId}
                    onClick={() => handlePartnerSelect(partnerId)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border text-sm transition-all ${
                      filter.selectedPartner === partnerId
                        ? "border-bnp-cyan bg-[#f0fdf4] text-main-900"
                        : "border-soft-200 hover:border-bnp-cyan text-grey-7"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 shrink-0 rounded border flex items-center justify-center ${
                        filter.selectedPartner === partnerId
                          ? "border-bnp-cyan bg-bnp-cyan"
                          : "border-soft-200"
                      }`}
                    >
                      {filter.selectedPartner === partnerId && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm font-medium capitalize">
                      {partnerId.replace(/-/g, " ")}
                    </span>
                  </button>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PartnerFilter;
